// SEMNELE DE CIRCULAȚIE ale subsolului, peste planșa de parcaj: plăcuțele
// «cedează» la gura aleilor secundare, săgețile de preselecție și de sens pe
// benzi, săgețile de la capul rampei (paternul planșei de referință A02 SUBSOL
// 1_C3 C4). Generatorul le așază (`architecture/circulatie/semne.py`) și le
// scrie în `diagnostics.semne_circulatie.niveluri[].semne` ca `{x, y, deg, fel}`, în
// coordonatele ABSOLUTE ale documentului, cu y-ul în sus și `deg` azimutul
// sensului de mers (0 = nord, 90 = est). Aici se face doar desenul: poziția
// trece prin `punctInCadru`, ca traseele de evacuare, iar unghiul pe ecran se
// citește din două puncte traduse (un sfert de tură pe grilele northing-first
// și oglindirea pe y nu se mai rescriu încă o dată).
//
// `fel` e un vocabular închis — `cedeaza`, `inainte`, `stanga`, `dreapta`,
// `inainte_stanga`, `inainte_dreapta`, `stanga_dreapta`, `inainte_stanga_dreapta`
// — mapat pe desen: triunghiul roșu pe plăcuță albă pentru cedează, iar pentru
// săgeți o singură formă compusă din tijă, vârf drept și ramuri, după pozele
// marcajelor rutiere (tija groasă, vârful de două ori mai lat, ramura iese din
// tijă și se termină în vârf). Toate glifele arată spre −y în reperul lor și se
// rotesc cu `rotate(deg)`, ca `SignPlate` și `RoadArrowMark` de la situație.

import { useMemo } from "react";
import { DECOR } from "./glyphs";

export type SemnCirculatie = { x: number; y: number; deg: number; fel: string };

/** Roșul plăcuței, același cu `A01.signRed` de la planșa de situație. */
const ROSU = "#d8261a";
const CONTUR = "#2a2a2a";
/** Dimensiunile desenate, cele cu care generatorul verifică locul
 *  (`circulatie.semne.SAGEATA_M`, `PLACUTA_M`): săgeata 3,0 × 1,0 m, plăcuța 1,9 m. */
const PLACUTA = 1.9;
/** Sub atâția pixeli pe metru semnele devin pete și se sting (ca `LOD.sign`
 *  de la situație). */
export const LOD_SEMNE = 3.2;

/** Conturul unei săgeți pictate, ca path SVG, TRASAT după pozele marcajelor
 *  rutiere trimise de Oana (21.10.2026), lungă de 3,0 m: săgeata dreaptă cu tija care se
 *  lărgește spre bază și vârful de două ori mai lat decât tija; virajul cu tija
 *  care se îndoaie și se termină într-un vârf înalt; «stânga-dreapta» ca un T
 *  cu două vârfuri; «înainte + viraj» cu ramura care iese din tijă într-un
 *  vârf lateral. Reperul: glifa arată spre −y (y de la −1,5 la +1,5), coordonatele în metri. Proporțiile sunt măsurate pe poze
 *  (vârful 0,38 din lungime, tija 0,14 → 0,19 din lungime la bază). */
export function caleaSagetii(fel: string): string {
  const parti = fel.split("_");
  const inainte = parti.includes("inainte");
  const stanga = parti.includes("stanga");
  const dreapta = parti.includes("dreapta");
  const f = (n: number) => +n.toFixed(3);
  // OGLINDA pe x a unui path (M/L/Q), cu ordinea punctelor ÎNTOARSĂ: oglinda
  // singură schimbă sensul de parcurgere, iar două subpath-uri parcurse în
  // sensuri opuse se anulează acolo unde se suprapun (regula nonzero) — ramura
  // ieșea cu o gaură pe tijă. Întoarse, toate subpath-urile rămân în același sens.
  const oglinda = (d: string) => {
    const seg = [...d.matchAll(/([MLQ])((?:\s*-?\d*\.?\d+\s+-?\d*\.?\d+)+)/g)]
      .map((m) => ({ c: m[1], p: m[2].trim().split(/\s+/).map(Number) }));
    const pct = (x: number, y: number) => `${f(-x)} ${y}`;
    const puncte = seg.map((g) => g.p.slice(-2));            // capătul fiecărui segment
    let out = `M${pct(puncte[puncte.length - 1][0], puncte[puncte.length - 1][1])}`;
    for (let i = seg.length - 1; i >= 1; i--) {
      const [px, py] = puncte[i - 1];
      out += seg[i].c === "Q" ? ` Q${pct(seg[i].p[0], seg[i].p[1])} ${pct(px, py)}` : ` L${pct(px, py)}`;
    }
    return out + " Z";
  };
  // săgeata DREAPTĂ (poza 4), centrată pe `dx`: vârf 1,15 lung și 0,94 lat,
  // tija 0,43 sub vârf și 0,57 la bază
  const dreapta_ = (dx: number) =>
    `M${f(dx)} -1.5 L${f(dx + 0.47)} -0.35 L${f(dx + 0.215)} -0.35 L${f(dx + 0.285)} 1.5 ` +
    `L${f(dx - 0.285)} 1.5 L${f(dx - 0.215)} -0.35 L${f(dx - 0.47)} -0.35 Z`;
  // RAMURA SPRE STÂNGA a unei săgeți «înainte + stânga» (poza 3, măsurată):
  // gâtul de 0,33 m iese din tijă în sus, se ARCUIEȘTE (ambele margini) și
  // ajunge orizontal în baza unui vârf lateral înalt de 0,89 și lung de 0,27,
  // cu tăișul la x = dx − 0,87. Scrisă în sensul tijei (acelor de ceas pe
  // ecran); racordurile cu tija stau puțin în ea, ca umplerea să fie continuă.
  const ramuraStanga = (dx: number) =>
    `M${f(dx - 0.18)} 0.0 L${f(dx - 0.18)} 0.55 Q${f(dx - 0.26)} 0.24 ${f(dx - 0.48)} 0.238 ` +
    `L${f(dx - 0.598)} 0.238 L${f(dx - 0.598)} 0.508 L${f(dx - 0.869)} 0.057 L${f(dx - 0.598)} -0.385 ` +
    `L${f(dx - 0.598)} -0.09 L${f(dx - 0.43)} -0.09 Q${f(dx - 0.25)} -0.09 ${f(dx - 0.18)} 0.0 Z`;
  // VIRAJUL singur (poza 2, scris pentru dreapta): tija se lărgește spre bază
  // (0,25 sus, 0,50 jos), sus se îndoaie spre dreapta și intră în baza unui
  // vârf înalt de 1,15 și lung de 0,38, al cărui colț de sus trece peste tijă
  const virajDreapta =
    "M-0.45 1.5 L-0.3 -0.85 Q-0.3 -1.2 0.0 -1.2 L0.12 -1.2 L0.12 -1.45 L0.5 -0.875 L0.12 -0.3 " +
    "L0.12 -0.8 L0.0 -0.8 Q-0.05 -0.75 -0.05 -0.6 L0.05 1.5 Z";
  // STÂNGA-DREAPTA (poza 5): două vârfuri laterale unite printr-o bară, tija sub ea
  const stangaDreapta =
    "M-0.84 -1.0 L-0.44 -1.5 L-0.44 -1.17 L0.44 -1.17 L0.44 -1.5 L0.84 -1.0 L0.44 -0.5 L0.44 -0.83 " +
    "L0.22 -0.83 L0.3 1.5 L-0.3 1.5 L-0.22 -0.83 L-0.44 -0.83 L-0.44 -0.5 Z";

  if (inainte) {
    // cu o singură ramură, tija se trage în partea opusă, ca pe poză, ca să încapă ramura
    const dx = stanga && !dreapta ? 0.21 : dreapta && !stanga ? -0.21 : 0;   // ca pe poză
    const out = [dreapta_(dx)];
    if (stanga) out.push(ramuraStanga(dx));
    if (dreapta) out.push(oglinda(ramuraStanga(-dx)));
    return out.join(" ");
  }
  if (stanga && dreapta) return stangaDreapta;
  if (dreapta) return virajDreapta;
  if (stanga) return oglinda(virajDreapta);
  return dreapta_(0);
}

function Semn({ x, y, deg, fel }: { x: number; y: number; deg: number; fel: string }) {
  if (fel === "cedeaza") {
    const R = PLACUTA / 2, h = R * 0.78;
    const tri = (k: number) => `0,${k * h} ${h * 0.92},${-k * h * 0.62} ${-h * 0.92},${-k * h * 0.62}`;
    // vârful triunghiului spre șofer (+y în reperul glifei = coada sensului)
    return (
      <g transform={`translate(${x} ${y}) rotate(${deg})`} {...DECOR}>
        <rect x={-R} y={-R} width={2 * R} height={2 * R} fill="#ffffff" stroke={CONTUR} strokeWidth={0.04} />
        <polygon points={tri(1)} fill={ROSU} />
        <polygon points={tri(1)} fill="#ffffff" transform="scale(0.52)" />
      </g>
    );
  }
  return (
    <g transform={`translate(${x} ${y}) rotate(${deg})`} {...DECOR}>
      <path d={caleaSagetii(fel)} fill="#ffffff" stroke={CONTUR} strokeWidth={0.06} strokeLinejoin="round" />
    </g>
  );
}

/** Unghiul pe ecran al azimutului `deg` din punctul `p`: direcția dintre
 *  punctul tradus și traducerea unui pas pe azimut; `rotate(θ)` duce −y al
 *  glifei pe ea. */
export function unghiInCadru(la: (p: number[]) => number[], p: number[], deg: number): number {
  const a = (deg * Math.PI) / 180;
  const [x0, y0] = la(p), [x1, y1] = la([p[0] + Math.sin(a), p[1] + Math.cos(a)]);
  return (Math.atan2(x1 - x0, -(y1 - y0)) * 180) / Math.PI;
}

export default function SemneCirculatie({ semne, la, pxPerM }: {
  semne: SemnCirculatie[];
  la: (p: number[]) => number[];
  pxPerM: number;
}) {
  const puse = useMemo(() => semne.map((s) => {
    const [x, y] = la([s.x, s.y]);
    return { x, y, deg: unghiInCadru(la, [s.x, s.y], s.deg), fel: s.fel };
  }), [semne, la]);
  if (pxPerM < LOD_SEMNE) return null;
  return (
    <g className="semne-circulatie" {...DECOR}>
      {puse.map((s, i) => <Semn key={i} {...s} />)}
    </g>
  );
}
